import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BusFront, Loader2, MapPin, LogIn, LogOut } from "lucide-react";
import parentApi from "@/lib/parentApi";

const fmtT = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export function StudentBus({ studentId }) {
  const nav = useNavigate();
  const [bus, setBus] = useState(null);
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [b, f] = await Promise.all([parentApi.get("/parent/bus"), parentApi.get("/parent/bus-feed")]);
        if (!alive) return;
        setBus(b.data.find((x) => x.student_id === studentId) || false);
        setFeed(f.data.filter((e) => e.student_id === studentId).slice(0, 20));
      } catch { if (alive) setBus(false); }
    };
    load();
    const iv = setInterval(load, 10000);
    return () => { alive = false; clearInterval(iv); };
  }, [studentId]);

  if (bus === null) return <div className="rounded-2xl bg-white p-10 text-center shadow-sm"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
  if (!bus) return <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-500" data-testid="parent-bus-none">No bus assigned to this child.</div>;

  return (
    <div className="space-y-3" data-testid="parent-student-bus">
      <div className="rounded-2xl bg-amber-500 text-slate-900 p-4 shadow-sm flex items-center gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-white/25 flex items-center justify-center"><BusFront className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Bus {bus.bus_number}{bus.driver_name ? ` · ${bus.driver_name}` : ""}</div>
          <div className="text-xs text-slate-800/80" data-testid="parent-bus-state">{bus.onboard ? "On board now" : "Not on the bus right now"}{bus.status && bus.status !== "on_time" ? ` · ${bus.status.replace("_", " ")}${bus.delay_minutes ? ` ${bus.delay_minutes} min` : ""}` : ""}</div>
        </div>
        <button onClick={() => nav(`/parent/bus?student=${studentId}`)} data-testid="parent-bus-map-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold"><MapPin className="h-3.5 w-3.5" /> Live map</button>
      </div>
      <section className="rounded-2xl bg-white shadow-sm p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Recent bus activity</div>
        {feed.length === 0 && <div className="text-sm text-slate-400">No bus events in the last 7 days.</div>}
        <ol className="space-y-2">
          {feed.map((e, i) => (
            <li key={i} className="flex items-center gap-3 text-sm" data-testid={`parent-bus-event-${i}`}>
              <span className={`h-7 w-7 rounded-full inline-flex items-center justify-center ${e.event_type === "board" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{e.event_type === "board" ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}</span>
              <span className="flex-1 text-slate-800">{e.event_type === "board" ? "Boarded" : "Got off"} bus {e.bus_number}</span>
              <span className="text-xs text-slate-500">{new Date(e.occurred_at).toLocaleDateString([], { month: "short", day: "numeric" })} · {fmtT(e.occurred_at)}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
